import { useEffect, useRef, useState } from "react";

const DEFAULT_FEATUREBASE_SRC = "https://do.featurebase.app/js/sdk.js";

const getExistingScript = (id, src) => {
  if (typeof document === "undefined") {
    return null;
  }

  return (
    document.getElementById(id) ||
    document.querySelector(`script[src="${src}"]`)
  );
};

const isFeaturebaseReady = () =>
  typeof window !== "undefined" && typeof window.Featurebase === "function";

const isScriptLoaded = (script) =>
  script?.getAttribute("data-loaded") === "true" || isFeaturebaseReady();

const getInitialStatus = (script) => {
  if (!script) {
    return "idle";
  }

  return isScriptLoaded(script) ? "loaded" : "loading";
};

const removeFeaturebaseElements = () => {
  if (typeof document === "undefined") {
    return;
  }

  const selectors = [
    "[id^='fb-messenger-root']",
    "[class^='featurebase-messenger-root']",
    "[class*=' featurebase']",
    "iframe[src*='featurebase']",
    `script[src*='${DEFAULT_FEATUREBASE_SRC}']`,
  ];

  document.querySelectorAll(selectors.join(",")).forEach((element) => {
    element.parentNode?.removeChild(element);
  });
};

const cleanupFeaturebase = (id, src) => {
  if (typeof document !== "undefined") {
    const script = getExistingScript(id, src);
    if (script) {
      script.parentNode?.removeChild(script);
    }
  }

  removeFeaturebaseElements();

  if (typeof window !== "undefined" && window.Featurebase) {
    delete window.Featurebase;
  }
};

const useFeaturebase = (options = {}) => {
  const {
    src = DEFAULT_FEATUREBASE_SRC,
    id = "featurebase-sdk",
    bootOptions,
    enabled = true,
    removeOnDisable = false,
  } = options;
  const [status, setStatus] = useState(() =>
    enabled ? getInitialStatus(getExistingScript(id, src)) : "idle"
  );
  const hasBootedRef = useRef(false);

  useEffect(() => {
    if (!enabled || typeof document === "undefined") {
      return undefined;
    }

    const existingScript = getExistingScript(id, src);
    if (existingScript) {
      if (isScriptLoaded(existingScript)) {
        setStatus("loaded");
        return undefined;
      }

      const handleLoad = () => {
        existingScript.setAttribute("data-loaded", "true");
        setStatus("loaded");
      };
      const handleError = () => setStatus("error");

      existingScript.addEventListener("load", handleLoad);
      existingScript.addEventListener("error", handleError);
      setStatus("loading");

      return () => {
        existingScript.removeEventListener("load", handleLoad);
        existingScript.removeEventListener("error", handleError);
      };
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;

    const handleLoad = () => {
      script.setAttribute("data-loaded", "true");
      setStatus("loaded");
    };
    const handleError = () => setStatus("error");

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);
    setStatus("loading");

    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
  }, [enabled, id, src]);

  useEffect(() => {
    if (enabled) {
      return;
    }

    if (!removeOnDisable) {
      setStatus("idle");
      hasBootedRef.current = false;
      return;
    }

    cleanupFeaturebase(id, src);
    setStatus("idle");
    hasBootedRef.current = false;
  }, [enabled, id, removeOnDisable, src]);

  useEffect(() => {
    if (!enabled || !bootOptions || hasBootedRef.current) {
      return;
    }

    if (status !== "loaded") {
      return;
    }

    if (!isFeaturebaseReady()) {
      return;
    }

    window.Featurebase("boot", bootOptions);
    hasBootedRef.current = true;
  }, [bootOptions, enabled, status]);

  return status;
};

export default useFeaturebase;
